/**
 * Created by Ning Jiang on 2021-10-11.
 */

import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {DeadwoodRip} from "../../DeadwoodRip";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {DeadwoodRipLayers, Layers} from "../DeadwoodRipLayers";
import {DeadwoodRipSound} from "../../sound/DeadwoodRipSound";
import {ShootableItem} from "./ShootableItem";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {gsap, TimelineLite} from "gsap";
import {DeadwoodRipBackground} from "../DeadwoodRipBackground";

export interface IShootConfig {
    timeline:TimelineLite,
    target:PIXI.Container,
    positions:PIXI.Point[], // TODO: test which position should be used.
    splashGetter?:() => PIXI.Sprite,
    holeGetter?:() => PIXI.Sprite,
    sparkGetter?:() => TimelineSprite,
    soundPlayer?:(stereoIndex:number) => TimelineLite,
    isFeatureShoot:boolean,
    playScreenEffects:boolean,
    alwaysReset:boolean,
    neverReset?:boolean,
    killed?:boolean,
    reelId?:number
}

export class ShootingScreen extends PIXI.Container {

    private _yellowShotPositions:{ position:PIXI.Point, rotation:number }[] = [
        {position : new PIXI.Point(540, 260), rotation : 21.4},
        {position : new PIXI.Point(532, -205), rotation : -40},
        {position : new PIXI.Point(553, 115), rotation : 9},
        {position : new PIXI.Point(-415, 293), rotation : 158.5},
        {position : new PIXI.Point(-469, -21), rotation : 192},
        {position : new PIXI.Point(448, 249), rotation : 27},
    ];

    public shootingItems:ShootableItem[] = [];
    private _resetItems:PIXI.Sprite[] = [];
    private _shotTls:TimelineLite[] = [];

    private _screenContainer:PIXI.Container;
    private _leftRed:PIXI.Sprite;
    private _rightRed:PIXI.Sprite;
    private _redTimeline:TimelineLite;

    private static TOTAL_SHOOT:number = 6;
    public shotLeft:number = ShootingScreen.TOTAL_SHOOT;
    private _screenInitialized:boolean = false;

    private _enabled:boolean = true;
    set enabled(value:boolean) {
        this._enabled = value;
        if(this._enabled) {
            this.shotLeft = ShootingScreen.TOTAL_SHOOT;
        }
        this.shootingItems.forEach(s => s.setEnabled(value));
        if(!this._enabled) {
            this.clear();
        }
    }

    get enabled():boolean {
        return this._enabled;
    }

    constructor() {
        super();

        this._screenContainer = new PIXI.Container();
        this.addChild(this._screenContainer);

        this.position.set(360, 360);
        DeadwoodRipLayers.getLayer(Layers.SCREEN_SHOOT)?.addChild(this);
    }

    public tryShot(config:IShootConfig):boolean {
        if(!this._enabled && !config.isFeatureShoot) {
            return false;
        }

        if(config.isFeatureShoot) {
            this.shotAt(config);
            return true;
        }

        if(this.shotLeft > 0) {
            this.shotAt(config);
            DeadwoodRipBackground.shakeStageOnce(0.5);
            this.shotLeft--;
            return true;
        }

        DeadwoodRipSound.soundPlayer.playTrack({name : DeadwoodRipSound.SHOOT_EMPTY});
        return false;
    }

    public shotAt(config:IShootConfig):void {
        const timeStamp = config.timeline.totalDuration();
        if(config.splashGetter) {
            // TODO:
        }

        if(config.holeGetter) {
            const hole = config.holeGetter();
            if(hole) {
                hole.position.set(config.positions[0].x, config.positions[0].y);
                hole.visible = false;
                // if(config.neverReset != true) {
                //     this._allShoots.push(hole);
                if(config.alwaysReset) {
                    this._resetItems.push(hole);
                }
                // }
                config.timeline.add(() => {
                    if(!DeadwoodRip.isSpinning) {
                        config.target.addChild(hole);
                    }
                }, timeStamp);
                config.timeline.add(() => {
                    if(!DeadwoodRip.isSpinning) {
                        if(hole && hole.parent) {
                            hole.visible = true
                        }
                    }
                }, timeStamp + 0.02);
            }
        }

        if(config.sparkGetter) {
            const spark = config.sparkGetter();
            spark.hide();
            spark.position.set(config.positions[0].x, config.positions[0].y);
            const sparkTl = new TimelineLite({paused : true});
            sparkTl.add(spark.getAnimationAutoShowHide());
            sparkTl.add(() => {
                if(spark && spark.parent) {
                    spark.parent.removeChild(spark);
                    spark.destroy();
                }
            });
            config.timeline.add(() => {
                if(!DeadwoodRip.isSpinning) {
                    config.target.addChild(spark);
                    sparkTl.play();
                }
            }, timeStamp);
        }

        if(config.positions.length > 1) {
            const windowHole:PIXI.Sprite = new PIXI.Sprite(DeadwoodRip.assetLoader.getTextures("shot/holes/HoleWindow")[0]);
            windowHole.anchor.set(0.5);
            const windowHolePosition = this._screenContainer.toLocal(config.positions[1]);
            windowHole.position.set(windowHolePosition.x, windowHolePosition.y);
            // windowHole.rotation = Math.random() * 2 * Math.PI;
            this._resetItems.push(windowHole);
            config.timeline.add(() => {
                if(!DeadwoodRip.isSpinning) {
                    this._screenContainer.addChild(windowHole);
                }
            }, timeStamp);
        }

        if(config.playScreenEffects) {
            config.timeline.add(() => {
                if(!DeadwoodRip.isSpinning) {
                    this.playScreenAnimation();
                    if(Math.random() > 0.6) {
                        DeadwoodRip.background.playShotEffects();
                    }
                }
            }, timeStamp);
        }

        if(config.soundPlayer) {
            config.timeline.add(
                () => {
                    if(!DeadwoodRip.isSpinning) {
                        config.soundPlayer!(this.getStereoIndex(config));
                    }
                },
                timeStamp);
        }

        this._shotTls.push(config.timeline);
    }

    private getStereoIndex(config:IShootConfig):number {
        if(config.reelId != null) {
            return config.reelId + 1;
        }

        if(config.positions.length > 1) {
            const windowHolePosition = this._screenContainer.toLocal(config.positions[1]);
            const xPosition = windowHolePosition.x;
            if(xPosition < -360) {
                return 0;
            }
            if(xPosition < -216) {
                return 1;
            }
            if(xPosition < -72) {
                return 2;
            }
            if(xPosition < 72) {
                return 3;
            }
            if(xPosition < 216) {
                return 4;
            }
            if(xPosition < 360) {
                return 5;
            }
            return 6;
        }

        return 3;
    }

    public playScreenAnimation():void {
        const baseName = DeadwoodRip.model.data.mode === GameMode.FREESPIN_SUPER ? "superFS" : "baseFS";

        if(this._redTimeline && this._redTimeline.isActive()) {
            this._redTimeline.pause();
            this._redTimeline.kill();
        }

        if(!this._screenInitialized) {
            this._leftRed = new PIXI.Sprite();
            this._leftRed.blendMode = PIXI.BLEND_MODES.ADD;
            this._leftRed.anchor.set(1, 0.5);
            this._leftRed.scale.x = -1;
            this._leftRed.alpha = 0;
            this.addChild(this._leftRed);

            this._rightRed = new PIXI.Sprite();
            this._rightRed.blendMode = PIXI.BLEND_MODES.ADD;
            this._rightRed.anchor.set(1, 0.5);
            this._rightRed.alpha = 0;
            this.addChild(this._rightRed);
            this._screenInitialized = true;

            this.onOrientationChanged();
        }

        this._leftRed.texture = DeadwoodRip.assetLoader.getTextures(`backgroundEffect/${baseName}/bloodB_02`)[0];
        this._rightRed.texture = DeadwoodRip.assetLoader.getTextures(`backgroundEffect/${baseName}/bloodB_02`)[0];

        const timeline = new TimelineLite();
        timeline.add([
            gsap.to(this._leftRed, {alpha : 0.87, duration : 0.067}),
            gsap.to(this._rightRed, {alpha : 0.87, duration : 0.067})
        ]);
        timeline.add([
            gsap.to(this._leftRed, {alpha : 0, duration : 0.533}),
            gsap.to(this._rightRed, {alpha : 0, duration : 0.533})
        ]);
        this._redTimeline = timeline;
        // }

        let yellow = new TimelineSprite(DeadwoodRip.assetLoader.getTextures(`backgroundEffect/${baseName}/gunFire/fire_`));
        yellow.anchor.set(0.5);
        yellow.scale.set(1.5);
        yellow.blendMode = PIXI.BLEND_MODES.ADD;
        const yellowConfig = ArrayHelper.getRandomValue(this._yellowShotPositions);
        yellow.position.set(yellowConfig.position.x, yellowConfig.position.y);
        yellow.rotation = yellowConfig.rotation * Math.PI / 180;
        this.addChild(yellow);
        yellow.getAnimationAutoShowHide(true, true, undefined, undefined, () => {
            yellow.parent.removeChild(yellow);
            yellow.destroy();
        });

        // randomly play redSpot
        if(Math.random() > 0.5) {
            return;
        }

        let red = new PIXI.Sprite(DeadwoodRip.assetLoader.getTextures(`backgroundEffect/${baseName}/bloodA`)[0]);
        red.anchor.set(0.5);
        red.blendMode = PIXI.BLEND_MODES.ADD;
        red.alpha = 0;
        red.scale.set(Math.random() * 0.15 + 0.65);
        red.position.set(MathHelper.randomNumberInRange(-300, 300), MathHelper.randomNumberInRange(-300, 300));
        const randomAlpha = Math.random() * 0.2 + 0.6;
        this.addChild(red);
        const redTimeline = gsap.timeline();
        redTimeline.to(red, {alpha : randomAlpha, duration : 0.067})
            .to(red, {
                alpha : 0, duration : 0.333,
                onComplete : () => {
                    red.parent.removeChild(red);
                    red.destroy();
                }
            });

        // blood splash
        if(Math.random() < this.getShootEffectFactor()) {
            const bloodSpine = new PIXI.spine.Spine(DeadwoodRip.assetLoader.getSpineAsset("shootVFX"));
            bloodSpine.autoUpdate = true;
            bloodSpine.position.set(0, 0);
            this.addChild(bloodSpine);
            bloodSpine.state.setAnimation(0, "bloodGlow", false);
            const tl = new TimelineLite();
            tl.add(() => {
            }, bloodSpine.spineData.findAnimation("bloodGlow").duration);
            if(Math.random() > 0.3) {
                const bloodName = `bloodSplash${MathHelper.randomIntInRange(1, 6)}`;
                tl.add(() => {bloodSpine.state.setAnimation(1, bloodName, false);}, 0);
                tl.add(() => {}, bloodSpine.spineData.findAnimation(bloodName).duration);
            }
            tl.add(
                () => {
                    bloodSpine.state.setEmptyAnimations(0);
                    bloodSpine.parent.removeChild(bloodSpine);
                    bloodSpine.destroy();
                });
        }

        // shooting line
        if(Math.random() < this.getShootEffectFactor()) {
            const lineSpine = new PIXI.spine.Spine(DeadwoodRip.assetLoader.getSpineAsset("shootVFX"));
            lineSpine.autoUpdate = true;
            lineSpine.position.set(MathHelper.randomNumberInRange(-300, 300), MathHelper.randomNumberInRange(-300, 300));
            this.addChild(lineSpine);
            const lineName = `fireLine${MathHelper.randomIntInRange(1, 4)}`;
            lineSpine.state.setAnimation(0, lineName, false);
            const tl = new TimelineLite();
            tl.add(() => {
            }, lineSpine.spineData.findAnimation(lineName).duration);
            tl.add(
                () => {
                    lineSpine.state.setEmptyAnimations(0);
                    lineSpine.parent.removeChild(lineSpine);
                    lineSpine.destroy();
                });
        }

    }

    private getShootEffectFactor():number {
        switch(DeadwoodRip.background.currentMode) {
            case GameMode.FREESPIN_SUPER:
                return 1;
            case GameMode.FREESPIN:
                return 0.7;
            case GameMode.NORMAL:
                return 0.3;
            default:
                return 0.3;
        }
    }

    public onOrientationChanged() {
        if(!this._screenInitialized) {
            return;
        }
        // if(!NolimitApplication.isLandscape) {
        if(this._redTimeline && this._redTimeline.isActive()) {
            this._redTimeline.pause();
            this._redTimeline.kill();
        }
        // this._leftRed.alpha = 0;
        // this._rightRed.alpha = 0;
        // this._screenContainer.scale.set(1, 1);
        // } else {
        // this._screenContainer.scale.set(1.12, 1.12);
        // }
        this._leftRed.position.set(-NolimitApplication.screenBounds.width * 0.5, 0);
        this._rightRed.position.set(NolimitApplication.screenBounds.width * 0.5, 0);
    }

    public clear():void {
        while(this._shotTls.length > 0) {
            const tl = this._shotTls.pop();
            if(tl && tl.isActive()) {
                tl.paused();
                tl.kill();
            }
        }
        while(this._resetItems.length > 0) {
            const item = this._resetItems.pop()!
            if(item && item.parent) {
                item.parent.removeChild(item);
                item.destroy();
            }
        }
    }
}